<template>
  <div class="screen-event-view has-tab-bar">
    <app-bar
      title="Deelnemers"
      :show-back-button="true"
      :back-button-path="backPath"
    />
    <spinner-loader v-if="status === 'loading'" />

    <template v-else-if="participantList">
      <div class="container">
        <div class="tab-bar" role="tablist" data-testid="participants-tab-bar">
          <div
            v-for="t in visibleTabs"
            :key="t.id"
            :data-tab="t.id"
            class="tab"
          >
            <button
              type="button"
              role="tab"
              :data-tab-variant="t.variant"
              :aria-selected="currentTab === t.id ? 'true' : 'false'"
              :aria-labelledby="'tab-label-' + t.id"
              :class="[
                'tab-button',
                `tab-button--${t.variant}`,
                currentTab === t.id
                  ? 'tab-button-active'
                  : 'tab-button-inactive'
              ]"
              @click="setTab(t.id)"
            >
              <component :is="t.icon" class="tab-icon" :stroke-width="2.25" />
              <span class="tab-count">{{ t.count }}</span>
            </button>
            <span
              :id="'tab-label-' + t.id"
              data-testid="participants-tab-label"
              :class="[
                'tab-label',
                currentTab === t.id ? 'tab-label-active' : 'tab-label-inactive'
              ]"
            >
              {{ t.label }}
            </span>
          </div>
        </div>

        <div v-if="dates && dates.length" class="form-select">
          <select
            v-model="selected"
            class="form-control"
            :style="{ border: borderSize + 'px solid ' + borderColor }"
            @change="setDate"
          >
            <option v-for="date in dates" :key="date.value" :value="date.value">
              {{ date.text }}
            </option>
          </select>
          <div
            class="select-arrow-background"
            :style="{ backgroundColor: selectArrowBackgroundColor }"
          />
          <div
            class="select-arrow"
            :style="{ borderTopColor: selectArrowColor }"
          />
        </div>

        <div v-if="userIsOwner" class="date-message" data-testid="date-message">
          <p
            v-if="messageSent"
            class="date-message-success"
            role="status"
            data-testid="date-message-sent"
          >
            {{
              $tc('event.participants.message.sent', messageSent.recipients, {
                date: messageSent.date,
                count: messageSent.recipients
              })
            }}
          </p>
          <button
            v-if="!messageOpen"
            type="button"
            class="button button-primary w-full"
            data-testid="date-message-open"
            @click="openMessage"
          >
            {{ $t('event.participants.message.open') }}
          </button>
          <form
            v-else
            novalidate
            data-testid="date-message-form"
            @submit.prevent="sendMessage"
          >
            <label for="date-message-text" class="date-message-label">
              {{
                selectedDateLabel
                  ? $t('event.participants.message.labelForDate', {
                      date: selectedDateLabel
                    })
                  : $t('event.participants.message.label')
              }}
            </label>
            <textarea
              id="date-message-text"
              v-model="messageText"
              class="form-control"
              rows="4"
              :maxlength="messageMaxLength"
              :disabled="messageSending"
              data-testid="date-message-text"
            />
            <p
              v-if="messageError"
              class="date-message-error"
              role="alert"
              data-testid="date-message-error"
            >
              {{ messageError }}
            </p>
            <div class="date-message-actions">
              <button
                type="button"
                class="button button-secondary"
                :disabled="messageSending"
                data-testid="date-message-cancel"
                @click="closeMessage"
              >
                {{ $t('event.participants.message.cancel') }}
              </button>
              <button
                type="submit"
                class="button button-primary"
                :disabled="messageSending"
                data-testid="date-message-submit"
              >
                {{
                  messageSending
                    ? $t('event.participants.message.sending')
                    : $t('event.participants.message.submit')
                }}
              </button>
            </div>
          </form>
        </div>

        <div v-if="currentTab === 'wel'">
          <template v-if="participantsYes.length > 0">
            <div class="wrapper-participant">
              <ul class="participant-list">
                <li
                  v-for="participant in participantsYes"
                  :key="participant.id"
                  class="participant-list-item"
                >
                  <div
                    v-if="participant.avatarUrl"
                    class="participant-avatar"
                    :style="{
                      backgroundImage: `url('${participant.avatarUrl}')`
                    }"
                  />
                  <div v-else class="participant-initials-avatar">
                    {{
                      getInitials(participant.firstName, participant.lastName)
                    }}
                  </div>
                  <div class="participant-name">
                    {{ `${participant.firstName} ${participant.lastName}` }}
                  </div>
                </li>
              </ul>
            </div>
          </template>
          <div v-else class="wrapper-participant">
            <p class="text-center">Er zijn geen deelnemers om te tonen</p>
          </div>
        </div>

        <div v-if="currentTab === '?'">
          <template v-if="participantsMaybe.length > 0">
            <div class="wrapper-participant">
              <ul class="participant-list">
                <li
                  v-for="participant in participantsMaybe"
                  :key="participant.id"
                >
                  <div
                    v-if="participant.avatarUrl"
                    class="participant-avatar"
                    :style="{
                      backgroundImage: `url('${participant.avatarUrl}')`
                    }"
                  />
                  <div v-else class="participant-initials-avatar">
                    {{
                      getInitials(participant.firstName, participant.lastName)
                    }}
                  </div>
                  <span class="participant-name">
                    {{ `${participant.firstName} ${participant.lastName}` }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
          <div v-else class="wrapper-participant">
            <p class="text-center">Er zijn geen deelnemers om te tonen</p>
          </div>
        </div>

        <div v-if="currentTab === 'niet'">
          <template v-if="participantsNo.length > 0">
            <div class="wrapper-participant">
              <ul class="participant-list">
                <li v-for="participant in participantsNo" :key="participant.id">
                  <div
                    v-if="participant.avatarUrl"
                    class="participant-avatar"
                    :style="{
                      backgroundImage: `url('${participant.avatarUrl}')`
                    }"
                  />
                  <div v-else class="participant-initials-avatar">
                    {{
                      getInitials(participant.firstName, participant.lastName)
                    }}
                  </div>
                  <span class="participant-name">
                    {{ `${participant.firstName} ${participant.lastName}` }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
          <div v-else class="wrapper-participant">
            <p class="text-center">Er zijn geen deelnemers om te tonen</p>
          </div>
        </div>

        <div v-if="currentTab === 'wachtlijst'">
          <template v-if="participantsWaitlisted.length > 0">
            <div class="wrapper-participant">
              <ul class="participant-list">
                <li
                  v-for="participant in participantsWaitlisted"
                  :key="participant.id"
                >
                  <div
                    v-if="participant.avatarUrl"
                    class="participant-avatar"
                    :style="{
                      backgroundImage: `url('${participant.avatarUrl}')`
                    }"
                  />
                  <div v-else class="participant-initials-avatar">
                    {{
                      getInitials(participant.firstName, participant.lastName)
                    }}
                  </div>
                  <span class="participant-name">
                    {{ `${participant.firstName} ${participant.lastName}` }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
          <div v-else class="wrapper-participant">
            <p class="text-center">Er staan geen deelnemers op de wachtlijst</p>
          </div>
        </div>
      </div>
    </template>

    <tab-bar selected="search" />
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import TabBar from '@/components/TabBar'
// import RoundIcon from '@/components/RoundIcon'
import SpinnerLoader from '@/components/SpinnerLoader'
import IconCheck from '@/components/icons/IconCheck'
import IconHelpCircle from '@/components/icons/IconHelpCircle'
import IconUserX from '@/components/icons/IconUserX'
import IconBell from '@/components/icons/IconBell'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import {
  EVENT_DATES_GET,
  EVENT_DATES_GET_ERROR,
  EVENT_DATES_GET_PARTICIPANTS,
  DATE_MESSAGE_PARTICIPANTS
} from '@/store/actions/date'
import api from '@/utils/api'
import config from '@/utils/config'

// The backend trims the message and accepts 1 to 2000 characters, and allows
// one message per date per 5 minutes (invi-nodejs-api#175).
const MESSAGE_MAX_LENGTH = 2000
const MESSAGE_COOLDOWN_SECONDS = 300
const MESSAGE_ERROR_CODES = [
  'MESSAGE_EMPTY',
  'MESSAGE_TOO_LONG',
  'NOT_AUTHORIZED',
  'EVENT_NOT_FOUND',
  'DATE_NOT_FOUND',
  'DATE_NOT_IN_EVENT',
  'MESSAGE_RATE_LIMITED'
]

export default {
  name: 'EventParticipantAttendance',
  components: {
    AppBar,
    TabBar,
    // RoundIcon,
    SpinnerLoader,
    IconCheck,
    IconHelpCircle,
    IconUserX,
    IconBell
  },
  data() {
    return {
      currentTab: 'wel', // ? niet wachtlijst
      participantsYes: [],
      participantsNo: [],
      participantsMaybe: [],
      participantsWaitlisted: [],
      selected: '',
      borderSize: 2,
      selectArrowColor: '#FFF',
      selectArrowBackgroundColor: '#009B6D',
      borderColor: '',
      routeName: this.$route.name,
      securityHash: this.$route.params.securityHash,
      dateId: this.$route.params.dateId,
      eventId: this.$route.params.id,
      userIsOwner: false,
      dates: [],
      backPath: '/',
      messageOpen: false,
      messageText: '',
      messageSending: false,
      messageError: '',
      messageSent: null,
      messageMaxLength: MESSAGE_MAX_LENGTH
    }
  },

  computed: {
    ...mapGetters(['authId', 'participantList']),
    ...mapState({
      eventDate: (state) => state.date.eventDate,
      status: (state) => state.date.status,
      dateParticipants: (state) => state.date.participants
    }),
    selectedDateLabel() {
      const date = (this.dates || []).find(
        (d) => String(d.value) === String(this.selected)
      )
      return date ? date.text : ''
    },
    showWaitlistTab() {
      return Boolean(
        config.featureFlags.waitlist &&
          this.eventDate &&
          this.eventDate.waitlistEnabled
      )
    },
    visibleTabs() {
      const tabs = [
        {
          id: 'wel',
          variant: 'yes',
          icon: 'IconCheck',
          label: this.$t('event.participants.tabs.yes'),
          count: this.participantsYes.length
        },
        {
          id: '?',
          variant: 'maybe',
          icon: 'IconHelpCircle',
          label: this.$t('event.participants.tabs.maybe'),
          count: this.participantsMaybe.length
        },
        {
          id: 'niet',
          variant: 'no',
          icon: 'IconUserX',
          label: this.$t('event.participants.tabs.no'),
          count: this.participantsNo.length
        }
      ]
      if (this.showWaitlistTab) {
        tabs.push({
          id: 'wachtlijst',
          variant: 'waitlist',
          icon: 'IconBell',
          label: this.$t('event.participants.tabs.waitlist'),
          count: this.participantsWaitlisted.length
        })
      }
      return tabs
    }
  },
  watch: {
    participantList(list) {
      this.participantsYes = list.yes || []
      this.participantsNo = list.no || []
      this.participantsMaybe = list.maybe || []
      this.participantsWaitlisted = list.waitlisted || []
    },
    eventDate: function (event) {
      this.userIsOwner = event.ownerId === parseInt(this.authId)
      this.dates =
        event.allDates &&
        event.allDates.map((date) => ({
          text: moment(date.startDateTime).format('dd D MMM HH:mm'),
          value: date.id
        }))
      this.selected = event.id
    }
  },
  created: async function () {
    let dateId = this.dateId
    if (
      this.routeName === 'EventParticipantAttendance' &&
      this.eventId &&
      this.securityHash
    ) {
      if (!dateId) {
        const response = await api.get(
          `event/${this.eventId}?securityHash=${this.securityHash}`
        )
        if (response && response.data && response.data.nextDate) {
          dateId = response.data.nextDate.id
        }
      }
      await this.$store.dispatch(EVENT_DATES_GET, {
        dateId,
        securityHash: this.securityHash
      })
      this.$store.dispatch(EVENT_DATES_GET_PARTICIPANTS, { dateId })
      this.backPath = `/events/${dateId}/${this.securityHash}`
      this.$router.push(`/events/${dateId}/${this.securityHash}/participants`)
    } else if (dateId) {
      this.backPath = `/events/${dateId}/${this.securityHash}`
      await this.$store.dispatch(EVENT_DATES_GET, {
        dateId,
        securityHash: this.securityHash
      })
      this.$store.dispatch(EVENT_DATES_GET_PARTICIPANTS, { dateId })
    } else {
      this.$store.commit(EVENT_DATES_GET_ERROR)
    }
  },
  methods: {
    setTab: function (tabName) {
      this.currentTab = tabName
    },
    setDate: function (event) {
      this.selected = event.target.value
      this.messageSent = null
      this.messageError = ''
      this.$store.dispatch(EVENT_DATES_GET_PARTICIPANTS, {
        dateId: event.target.value
      })
    },
    formatTime: function (timestamp, timezoneOffset) {
      const eventCreatedInSummer = timezoneOffset === 2
      return moment(timestamp).isDST()
        ? moment(timestamp)
            .subtract(eventCreatedInSummer ? 0 : 1, 'hours')
            .locale('nl')
            .format('dd D MMM HH:mm')
        : moment(timestamp)
            .add(eventCreatedInSummer ? 1 : 0, 'hours')
            .locale('nl')
            .format('dd D MMM HH:mm')
    },
    openMessage: function () {
      this.messageOpen = true
      this.messageSent = null
      this.messageError = ''
    },
    closeMessage: function () {
      this.messageOpen = false
      this.messageError = ''
    },
    sendMessage: async function () {
      const message = this.messageText.trim()
      if (!message) {
        this.messageError = this.messageErrorText('MESSAGE_EMPTY')
        return
      }
      if (message.length > MESSAGE_MAX_LENGTH) {
        this.messageError = this.messageErrorText('MESSAGE_TOO_LONG')
        return
      }
      this.messageSending = true
      this.messageError = ''
      try {
        const result = await this.$store.dispatch(DATE_MESSAGE_PARTICIPANTS, {
          eventId: this.eventDate.eventId,
          dateId: this.selected || this.eventDate.id,
          message
        })
        this.messageSent = {
          recipients: Number((result && result.recipients) || 0),
          date:
            result && result.startDateTime
              ? moment(result.startDateTime).format('dd D MMM HH:mm')
              : this.selectedDateLabel
        }
        this.messageText = ''
        this.messageOpen = false
      } catch (err) {
        // Keep `messageText` so the organizer does not lose what they typed.
        const error = ((err && err.response && err.response.data) || {}).error
        this.messageError = this.messageErrorText(
          error && error.code,
          error || {}
        )
      } finally {
        this.messageSending = false
      }
    },
    // `maxLength` / `cooldownSeconds` sit in `properties` on the LoopBack
    // error, which is only passed on in debug mode, so both fall back to the
    // known limits.
    messageErrorText: function (code, error = {}) {
      const props = error.properties || {}
      if (!MESSAGE_ERROR_CODES.includes(code)) {
        return this.$t('event.participants.message.errors.generic')
      }
      if (code === 'MESSAGE_RATE_LIMITED') {
        const seconds =
          Number(props.cooldownSeconds || error.cooldownSeconds) ||
          MESSAGE_COOLDOWN_SECONDS
        const minutes = Math.max(1, Math.ceil(seconds / 60))
        return this.$tc(`event.participants.message.errors.${code}`, minutes, {
          count: minutes
        })
      }
      if (code === 'MESSAGE_TOO_LONG') {
        return this.$t(`event.participants.message.errors.${code}`, {
          maxLength:
            Number(props.maxLength || error.maxLength) || MESSAGE_MAX_LENGTH
        })
      }
      return this.$t(`event.participants.message.errors.${code}`)
    },
    getInitials: function (firstName, lastName) {
      return (
        (firstName ? firstName.charAt(0) : '') +
        (lastName ? lastName.charAt(0) : '')
      ).toUpperCase()
    },
    paidIconStyle() {
      return {
        fontSize: this.size * 0.6 + 'px',
        color: '#fff'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

h1.event-title {
  margin-bottom: 0;
  margin-top: 1.5rem;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
}

// Participant tabs — issue #68.
// Figma design: icon + count INSIDE each tab button, small label BELOW.
// Each tab has its own active colour keyed by `data-tab-variant`.
// Inactive state is a neutral gray across all variants.
//
// Per-tab active colours (sourced from Figma):
//   yes      -> #3a9e7e  (emerald — brand accent used in the new design)
//   maybe    -> #374151  (gray-700)
//   no       -> #ef4444  (red-500)
//   waitlist -> #f59e0b  (amber-500)
//
// These are scoped locally because the palette is not yet reused
// anywhere else in the app.
$tabYesActive: #3a9e7e;
$tabMaybeActive: #374151;
$tabNoActive: #ef4444;
$tabWaitlistActive: #f59e0b;

$tabInactiveBg: #f3f4f6; // gray-100
$tabInactiveFg: #6b7280; // gray-500
$tabInactiveHoverBg: #e5e7eb; // gray-200
$tabInactiveHoverFg: #374151; // gray-700

$tabLabelActive: #374151; // gray-700
$tabLabelInactive: #9ca3af; // gray-400

.tab-bar {
  display: flex;
  gap: 8px;
  // The shared AppBar leaves no buffer on iOS (mb-28 exactly equals
  // IosButton height + AppBar height) and only 16px on desktop, so a
  // small margin here would visually touch the title bar on iOS.
  margin-top: 1.5rem;
  margin-bottom: 15px;
  width: 100%;
}

.tab {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.tab-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 6px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
  white-space: nowrap;
  -webkit-appearance: none;
  appearance: none;
  background-color: $tabInactiveBg;
  color: $tabInactiveFg;
}

.tab-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.tab-count {
  line-height: 1;
}

// Inactive state is shared across all variants.
.tab-button-inactive {
  background-color: $tabInactiveBg;
  color: $tabInactiveFg;
  border-color: transparent;
}

.tab-button-inactive:hover,
.tab-button-inactive:focus {
  background-color: $tabInactiveHoverBg;
  color: $tabInactiveHoverFg;
}

// Active state — per-variant colour.
.tab-button-active {
  color: $ipWhite;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.tab-button--yes.tab-button-active {
  background-color: $tabYesActive;
  border-color: $tabYesActive;
}
.tab-button--maybe.tab-button-active {
  background-color: $tabMaybeActive;
  border-color: $tabMaybeActive;
}
.tab-button--no.tab-button-active {
  background-color: $tabNoActive;
  border-color: $tabNoActive;
}
.tab-button--waitlist.tab-button-active {
  background-color: $tabWaitlistActive;
  border-color: $tabWaitlistActive;
}

.tab-label {
  display: block;
  margin-top: 4px;
  text-align: center;
  font-size: 0.68rem;
  line-height: 1.2;
  font-weight: 500;
  // Labels need to fit four-across on 320 px. Truncate rather than wrap.
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-label-active {
  color: $tabLabelActive;
}

.tab-label-inactive {
  color: $tabLabelInactive;
}

@media (max-width: 360px) {
  .tab-bar {
    gap: 6px;
  }
  .tab-button {
    gap: 4px;
    padding: 7px 4px;
    font-size: 0.78rem;
  }
  .tab-icon {
    width: 14px;
    height: 14px;
  }
  .tab-label {
    font-size: 0.64rem;
  }
}

@media (max-width: 325px) {
  .tab-bar {
    gap: 4px;
  }
  .tab-button {
    gap: 3px;
    padding: 6px 3px;
    font-size: 0.72rem;
  }
  .tab-icon {
    width: 13px;
    height: 13px;
  }
  .tab-label {
    font-size: 0.6rem;
  }
}

.page-title {
  margin: 30px 0;
}

.form-select {
  position: relative;
  display: inline-block;
  width: 100%;
}
.form-select select {
  display: inline-block;
  width: 100%;
  outline: 0;
  border: 1px solid $ipBlack;
  background: $ipWhite;
  color: $ipBlack;
  font-size: 1.25rem;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.form-select select::-ms-expand {
  display: none;
}
.form-select select:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.select-arrow {
  position: absolute;
  top: 19px;
  right: 15px;
  width: 0;
  height: 0;
  pointer-events: none;
  border-style: solid;
  border-width: 12px 10px 0 10px;
  border-color: $ipWhite transparent transparent transparent;
}
.select-arrow-background {
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 50px;
  background: $ipGreen;
  pointer-events: none;
}

.wrapper-participant {
  margin-top: 30px;
}
.participant-list {
  list-style: none;
  padding: 0;
  margin-bottom: 0;
}
.participant-list li {
  display: flex;
  margin: 15px 0 30px 0;
  text-align: left;
  width: 100%;
  align-items: center;
}
.participant-list li .participant-initials-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  background: #3a9e7e;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.participant-list li .participant-avatar {
  display: inline-block;
  width: 48px;
  height: 48px;
  background-size: cover;
  background-position: center;
  position: relative;
  white-space: nowrap;
  vertical-align: middle;
  padding: 0;
  outline: 0;
  border-radius: 9999px;
  flex-shrink: 0;
}
.participant-list li a {
  text-decoration: none;
  color: inherit;
}
.participant-list li .participant-name {
  flex: 1;
  font-size: 1rem;
  margin-left: 1rem;
}
.to-participants {
  margin-bottom: 30px;
}
@media (max-width: 320px) {
  .participant-list li {
    width: 85px;
  }
  .participant-list li .participant-name {
    padding: 0 5px;
  }
}
.status-icon {
  display: block;
  float: right;
  height: 24px;
  border-radius: 12px;
  background-color: rgb(230, 230, 230);
  color: white;
  font-size: 0.875rem;
  padding: 2px 0.5rem;
  margin: 0 auto;
}
.status-icon.paid {
  background-color: $ipGreen;
}
.status-icon.not-paid {
  background-color: rgb(230, 230, 230);
  color: #212529;
}
.icon {
  margin-right: 0.25rem;
}

.date-message {
  margin-top: 20px;
}
.date-message-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}
.date-message textarea {
  height: auto;
  font-size: 1rem;
}
.date-message-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 0.75rem;
}
.date-message-error {
  margin: 0.5rem 0 0;
  color: #ef4444;
}
.date-message-success {
  margin-bottom: 0.75rem;
  color: #3a9e7e;
}
</style>
